import type { Meta, SelectMeta, ValuesType } from '../types'
import type { CommonProps, ProviderContext } from './ContextProvider'

import React, { useEffect, useRef, useState } from 'react'
import {
	entries,
	filter,
	fromPairs,
	isEqual,
	pickAll,
	pipe,
	uniq,
} from 'lodash/fp'
import { PlusIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { cn } from '@/shared/lib/utils'

import { cloneObj, isEqualArr, stringCompare } from '../utils'
import { useRefinementBar } from './ContextProvider'
import { FilterButton } from './FilterButton'
import { FilterManager } from './FilterManager'

type RefinementBarProps = {
	/** The key of the "active" popup; use this with `onPopupOpen` and `onPopupClose` to take control of the field popups. */
	activePopupKey?: string | null
	/** Called when a field popup is opened, with the field key. */
	onPopupOpen?: (key: string) => void
	/** Called when a field popup is closed. */
	onPopupClose?: () => void
	/** The maximum number of fields to display before collapsing. */
	limit?: number
	/** Whether the refinement bar should be expandable. */
	isExpandable?: boolean
	/** Chips scroll horizontally; the add button stays pinned on the left. */
	isScrollable?: boolean
}

export function RefinementBar(
	props: RefinementBarProps & { context: ProviderContext }
) {
	const { t } = useTranslation()

	const { isExpandable = false, isScrollable = false, limit = 5 } = props
	const [activePopupKey, setActivePopupKey] = useState<string | null>(null)
	const [invalidState, setInvalidState] = useState<Record<string, string>>({})
	const [isExpanded, setIsExpanded] = useState<boolean>(true)
	const [stateValues, setStateValues] = useState<ValuesType>({})
	useEffect(() => {
		setStateValues(props.context.value)
	}, [props.context.value])
	const showLessRef = useRef<HTMLButtonElement | null>(null)
	const showAllRef = useRef(null)
	const mapKeyToOption = (value: string) => {
		const field = props.context.fieldConfig[value]
		const label = field?.label || value

		return { label, value }
	}
	const filterOptions = props.context.removeableKeys.map(mapKeyToOption)
	const getActivePopup = () =>
		props.activePopupKey === undefined ? activePopupKey : props.activePopupKey
	const openPopup = (key: string) => {
		const { onPopupOpen } = props
		if (onPopupOpen) onPopupOpen(key)
		setActivePopupKey(key)
	}
	const closePopup = () => {
		const { onPopupClose } = props

		if (onPopupClose) onPopupClose()
		setActivePopupKey(null)
	}
	const handleFieldAdd = async (key: string) => {
		const field = props.context.fieldConfig[key]
		const data = field.getInitialValue()
		const meta: Meta = { action: 'add', key, data }
		const values = await cloneObj(stateValues, { add: { [key]: data } })

		openPopup(key)
		setStateValues(values)
		isExpandable && setIsExpanded(true)
		props.context.onChange(values, meta)
	}
	const handleFieldRemove = async (key: string, event?: Event) => {
		if (event) event.preventDefault()
		const values = await cloneObj(stateValues, { remove: key })
		setStateValues(values)
		props.context.onChange(values, { action: 'remove', key })
	}
	const handleBulkFieldRemove = (keys: string[]) => {
		let values = stateValues
		for (let i = 0; i < keys.length; i++) {
			const key = keys[i]
			values = cloneObj(stateValues, { remove: key })
		}
		setStateValues(values)
		props.context.onChange(values, { action: 'bulk-remove', keys })
	}
	const handleFieldClear = (key: string) => {
		const field = props.context.fieldConfig[key]
		const value = field.getInitialValue()
		const values = cloneObj(stateValues, { add: { [key]: value } })
		setStateValues(values)
		props.context.onChange(values, { action: 'clear', key })
	}
	const handleFieldReset = (key: string) => () => {
		const originalValue = props.context.value[key]
		if (!originalValue) {
			const values = cloneObj(stateValues, { remove: key })
			setStateValues(values)
			setInvalidState(cloneObj(invalidState, { remove: key }))
			props.context.onChange(values, { action: 'reset', key } as Meta)
		} else {
			const values = cloneObj(stateValues, { add: { [key]: originalValue } })
			setInvalidState(cloneObj(invalidState, { remove: key }))
			setStateValues(values)
			props.context.onChange(values, { action: 'reset', key } as Meta)
		}
	}
	const getValidValues = pipe([
		entries,
		filter(([key]) => !invalidState[key]),
		fromPairs,
	])
	const handleFieldApply = (key: string) => () => {
		if (stateValues[key] === props.context.value[key]) {
			return
		}
		const { fieldConfig } = props.context
		const value = stateValues[key]
		const oldInvalid = invalidState
		const field = fieldConfig[key]
		const invalidMessage = field.validate(value, t)
		// eslint-disable-next-line
		let invalid = oldInvalid
		if (invalidMessage) {
			invalid = cloneObj(oldInvalid, { add: { [key]: invalidMessage } })
			setInvalidState(invalid)

			return
		} else if (oldInvalid[key]) {
			invalid = cloneObj(oldInvalid, { remove: key })
			setInvalidState(invalid)
		}
		const data = stateValues[key]
		const meta: Meta = { action: 'update', key, data }

		props.context.onChange(getValidValues(stateValues), meta)
	}
	const handleFieldChange =
		(key: string) => (value: string | number | boolean | string[]) => {
			const values = cloneObj(stateValues, { add: { [key]: value } })
			setStateValues({ ...values })
		}
	const makeField = (config?: { isRemovable?: boolean }) => (key: string) => {
		const fieldModel = props.context.fieldConfig[key]

		// Catch invalid configurations
		if (!fieldModel) {
			const likelySource = config?.isRemovable ? 'value' : 'irremovableKeys'

			console.error(
				`Couldn't find a matching field config for key "${key}". There may be stale or invalid keys in \`${likelySource}\`.`
			)

			return null
		}

		const field = fieldModel
		const FieldView = field.type.view as React.ComponentType<any>

		// Catch missing views:
		// This should only really happen when developing a new field type
		if (!FieldView) {
			console.error(
				`Couldn't find the View (${field.type.name}) for key "${key}".`
			)

			return null
		}

		const invalidMessage = invalidState[key]
		const isInvalid = Boolean(invalidMessage)

		// Values
		const initialValue = field.getInitialValue()
		const storedValue = props.context.value[key] || initialValue
		const localValue = stateValues[key] || initialValue

		const popupIsOpen = getActivePopup() === key

		const fieldUI = (renderContextProps: object) => {
			const extra = { ...config, ...renderContextProps }

			return (
				<FieldView
					closePopup={closePopup}
					field={field}
					invalidMessage={invalidMessage}
					key={key}
					isDirty={
						!isEqual(
							pickAll(['operation', 'value'], storedValue),
							pickAll(['operation', 'value'], localValue)
						)
					}
					onClear={() => handleFieldClear(key)}
					onReset={handleFieldReset(key)}
					onRemove={() => handleFieldRemove(key)}
					onChange={handleFieldChange(key)}
					onApply={handleFieldApply(key)}
					refinementBarValue={props.context.value}
					storedValue={storedValue}
					localValue={localValue}
					{...extra}
				/>
			)
		}

		return (
			<Popover
				open={popupIsOpen}
				onOpenChange={(open) => {
					if (open) {
						openPopup(key)
					} else {
						if (
							!isEqual(
								pickAll(['operation', 'value'], storedValue),
								pickAll(['operation', 'value'], localValue)
							)
						) {
							if (
								isEqual(
									pickAll(['operation', 'value'], storedValue),
									pickAll(['operation', 'value'], initialValue)
								)
							) {
								setStateValues(cloneObj(stateValues, { remove: key }))
							} else {
								setStateValues(
									cloneObj(stateValues, { add: { [key]: storedValue } })
								)
							}
						}

						closePopup()
					}
				}}
			>
				<PopoverTrigger asChild>
					<FilterButton
						isInvalid={isInvalid}
						isSelected={popupIsOpen}
						onClear={
							stringCompare(storedValue, initialValue)
								? undefined
								: () => handleFieldClear(key)
						}
					>
						{field.formatLabel(storedValue)}
					</FilterButton>
				</PopoverTrigger>
				<PopoverContent className='w-auto p-0'>{fieldUI({})}</PopoverContent>
			</Popover>
		)
	}
	const onChangeFilter = (options: string[], meta: SelectMeta) => {
		switch (meta.action) {
			case 'clear-options':
				handleBulkFieldRemove(options)
				break
			case 'select-option':
				handleFieldAdd(meta.option!)
				break
			case 'deselect-option':
				handleFieldRemove(meta.option!)
				break
			default:
		}
		closePopup()
	}
	const showAll = (isExpanded: boolean) => () => {
		setIsExpanded(isExpanded)
		const target = isExpanded ? showLessRef.current : showAllRef.current
		if (target && typeof target.focus === 'function') {
			target.focus()
		}
	}
	const shouldDisplayAddUI = () => {
		const { fieldKeys, irremovableKeys, selectedKeys } = props.context

		const visibleSelectedKeys = selectedKeys.filter(
			(key) => props.context.fieldConfig?.[key]
		)

		return (
			!isEqualArr(fieldKeys, irremovableKeys) &&
			uniq([...irremovableKeys, ...visibleSelectedKeys])?.length < limit
		)
	}
	const { irremovableKeys, selectedKeys } = props.context
	const FILTER_POPUP_KEY = '__refinement-bar-more-menu__'

	return (
		<div
			className={cn(
				'flex grow-0 flex-nowrap gap-1',
				isScrollable && 'min-w-0 items-center'
			)}
		>
			{/* p-1/-m-1 leaves room so overflow-x clipping doesn't cut focus rings */}
			<div
				className={cn(
					'flex flex-nowrap gap-1',
					isScrollable &&
						'-m-1 min-w-0 scroll-fade-x scrollbar-none overflow-x-auto p-1 *:shrink-0'
				)}
			>
				{irremovableKeys.map(makeField({ isRemovable: false }))}
				{isExpanded && selectedKeys.map(makeField({ isRemovable: true }))}

				{/* Show More/Less Control */}
				{!isExpanded && selectedKeys.length ? (
					<Button ref={showAllRef} size='sm' onClick={showAll(true)}>
						{t('general_show_more', { value: selectedKeys.length })}
					</Button>
				) : null}
			</div>
			{/* Add Filter Popup */}
			{shouldDisplayAddUI() ? (
				<Popover
					open={getActivePopup() === FILTER_POPUP_KEY}
					onOpenChange={(open) => {
						open ? openPopup(FILTER_POPUP_KEY) : closePopup()
					}}
				>
					<Tooltip delayDuration={500}>
						<TooltipTrigger asChild>
							<PopoverTrigger asChild>
								<Button variant='dashed' className='shrink-0' size='icon-sm'>
									<PlusIcon />
								</Button>
							</PopoverTrigger>
						</TooltipTrigger>
						<TooltipContent>
							{t('app.add_quick_filter', {
								defaultValue: 'Add quick filter',
							})}
						</TooltipContent>
					</Tooltip>
					<PopoverContent className='w-auto p-0'>
						<div className='min-w-55'>
							<FilterManager
								options={filterOptions}
								onChange={onChangeFilter}
								value={selectedKeys}
							/>
						</div>
					</PopoverContent>
				</Popover>
			) : null}

			{isExpandable && isExpanded && selectedKeys.length ? (
				<Button
					size='sm'
					ref={showLessRef}
					className={cn(isScrollable && 'shrink-0')}
					onClick={showAll(false)}
				>
					{t('app.show_less', { defaultValue: 'Show less' })}
				</Button>
			) : null}
		</div>
	)
}

type RefinementBarControlledProps = CommonProps & RefinementBarProps

const RefinementBarControlled = ({
	initialKeys,
	activePopupKey,
	fieldConfig,
	irremovableKeys,
	onChange,
	onPopupClose,
	onPopupOpen,
	value,
	isExpandable,
	isScrollable,
	limit,
}: RefinementBarControlledProps) => {
	const context = useRefinementBar({
		fieldConfig,
		initialKeys,
		irremovableKeys,
		onChange,
		value,
	})

	return (
		<RefinementBar
			activePopupKey={activePopupKey}
			onPopupOpen={onPopupOpen}
			onPopupClose={onPopupClose}
			context={context}
			isExpandable={isExpandable}
			isScrollable={isScrollable}
			limit={limit}
		/>
	)
}

export default RefinementBarControlled
